import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FormControlLabel, Switch } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import VoteHeadCard from "../components/voteHeads/VoteHeadCard";
import CreateVoteHeadModal from "../components/voteHeads/CreateVoteHeadModal";
import UpdateVoteHeadModal from "../components/voteHeads/UpdateVoteHeadModal";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getVoteHeads,
  deleteVoteHead,
  resetVoteHeadServerStatus,
} from "../redux/actions/voteHeadActions";
import { VoteHeadResponseDto } from "../shared/dtos/voteHead/VoteHeadResponseDto";

const VoteHeadsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- REDUX SELECTORS ---
  const {
    items: voteHeads,
    loadingFetchAll: loadingVoteHeads,
    errorFetchAll: errorLoadingVoteHeads,
    serverStatus,
    loadingCreate: loadingVoteHeadCreate,
    errorCreate: errorCreatingVoteHead,
    loadingUpdate: loadingVoteHeadUpdate,
    errorUpdate: errorUpdatingVoteHead,
    loadingDelete: loadingVoteHeadDelete,
  } = useAppSelector((state) => state.voteHead);

  // --- LOCAL FILTER STATE ---
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [recurringOnly, setRecurringOnly] = useState<boolean>(false);
  const [optionalOnly, setOptionalOnly] = useState<boolean>(false);

  // --- MODAL STATE ---
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedVoteHead, setSelectedVoteHead] =
    useState<VoteHeadResponseDto | null>(null);

  // --- PERMISSIONS ---
  const canCreate = hasPermission("vote_head:create");
  const canUpdate = hasPermission("vote_head:update");
  const canDelete = hasPermission("vote_head:delete");

  // --- FETCH DATA ---
  const fetchVoteHeads = useCallback((): void => {
    dispatch(getVoteHeads());
  }, [dispatch]);

  useEffect(() => {
    fetchVoteHeads();
  }, [fetchVoteHeads]);

  // --- FRONTEND FILTERING & SORTING ---
  const filteredVoteHeads = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return voteHeads
      .filter((vh) => {
        if (recurringOnly && !vh.recurring) return false;
        if (optionalOnly && !vh.isOptional) return false;

        if (!query) return true;

        const matchesName = vh.name?.toLowerCase().includes(query);
        const matchesDescription = vh.description
          ?.toLowerCase()
          .includes(query);

        return Boolean(matchesName || matchesDescription);
      })
      .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  }, [voteHeads, searchTerm, recurringOnly, optionalOnly]);

  // --- MODAL HANDLERS ---
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);

  const handleCloseCreate = useCallback((): void => {
    if (!loadingVoteHeadCreate) setIsCreateModalOpen(false);
  }, [loadingVoteHeadCreate]);

  const handleOpenUpdate = (voteHead: VoteHeadResponseDto): void => {
    setSelectedVoteHead(voteHead);
    setIsUpdateModalOpen(true);
  };

  const handleCloseUpdate = useCallback((): void => {
    if (!loadingVoteHeadUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedVoteHead(null);
    }
  }, [loadingVoteHeadUpdate]);

  const handleOpenDelete = (voteHead: VoteHeadResponseDto): void => {
    setSelectedVoteHead(voteHead);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDelete = useCallback((): void => {
    if (!loadingVoteHeadDelete) {
      setIsDeleteModalOpen(false);
      setSelectedVoteHead(null);
    }
  }, [loadingVoteHeadDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedVoteHead) {
      dispatch(deleteVoteHead(selectedVoteHead.id));
    }
  };

  // --- SERVER STATUS WATCHER ---
  useEffect(() => {
    if (!serverStatus) return;

    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) {
      handleCloseCreate();
    }
    if (operation === "update" && status === 200) {
      handleCloseUpdate();
    }
    if (operation === "delete" && status === 200) {
      handleCloseDelete();
    }

    dispatch(resetVoteHeadServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="Vote Heads"
      subtitle="Manage financial vote heads, fee categories, and priorities."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Vote Head
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search vote heads by name or description..."
      >
        <FormControlLabel
          control={
            <Switch
              checked={recurringOnly}
              onChange={(e) => setRecurringOnly(e.target.checked)}
              color="primary"
            />
          }
          label="Recurring Only"
          sx={{
            userSelect: "none",
            "& .MuiFormControlLabel-label": {
              fontSize: "0.875rem",
              fontWeight: 500,
            },
          }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={optionalOnly}
              onChange={(e) => setOptionalOnly(e.target.checked)}
              color="primary"
            />
          }
          label="Optional Only"
          sx={{
            userSelect: "none",
            "& .MuiFormControlLabel-label": {
              fontSize: "0.875rem",
              fontWeight: 500,
            },
          }}
        />
      </PageToolbar>

      <CardGridContainer
        loading={loadingVoteHeads}
        error={errorLoadingVoteHeads}
        itemCount={filteredVoteHeads.length}
        emptyMessage="No vote heads match your search criteria"
        onRetry={fetchVoteHeads}
      >
        {filteredVoteHeads.map((voteHead) => (
          <VoteHeadCard
            key={voteHead.id}
            voteHead={voteHead}
            onEdit={canUpdate ? () => handleOpenUpdate(voteHead) : undefined}
            onDelete={canDelete ? () => handleOpenDelete(voteHead) : undefined}
          />
        ))}
      </CardGridContainer>

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateVoteHeadModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingVoteHeadCreate}
          error={errorCreatingVoteHead}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && selectedVoteHead && (
        <UpdateVoteHeadModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          voteHead={selectedVoteHead}
          loading={loadingVoteHeadUpdate}
          error={errorUpdatingVoteHead}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteModalOpen && selectedVoteHead && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Vote Head"
          description={`Are you sure you want to delete "${selectedVoteHead.name}"? This action cannot be undone.`}
          loading={loadingVoteHeadDelete}
        />
      )}
    </PageContainer>
  );
};

export default VoteHeadsPage;
